import { useEffect, useState, useRef } from "react";
import * as EventSourcePolyfill from "../helpers/eventsource-polyfill.js";

// Import children components to plug in and render.
import MessagingHeader from "./messagingHeader";
import MessagingBody from "./messagingBody";
import MessagingInputFooter from "./messagingInputFooter";
import GalleryPanel from "./galleryPanel";
import * as AwacTemplateRegistry from "../awac/templateRegistry";

import { setJwt, setLastEventId, storeConversationId, getConversationId, getJwt, clearInMemoryData, setDeploymentConfiguration } from "../services/dataProvider";
import { subscribeToEventSource, closeEventSource } from '../services/eventSourceService';
import { sendTypingIndicator, sendTextMessage, getContinuityJwt, listConversations, listConversationEntries, closeConversation, getUnauthenticatedAccessToken, createConversation } from "../services/messagingService";
import * as ConversationEntryUtil from "../helpers/conversationEntryUtil";
import { CONVERSATION_CONSTANTS, STORAGE_KEYS, CLIENT_CONSTANTS, AWAC_CONSTANTS } from "../helpers/constants";
import { setItemInWebStorage, clearWebStorage } from "../helpers/webstorageUtils";
import { util } from "../helpers/common";
import { track, AwacEvents } from "../awac/analytics";
import { getAwacState, setAwacState, clearAwacState, clearAllAwacState } from "../awac/awacSessionState";
import { applyBookingConfirmFallback } from "../awac/confirmFallback";
import { sendMapCommand } from "../awac/poiMapBridge";
import { prechatUtil } from "../helpers/prechatUtil.js";
import Prechat from "./prechat.js";
import CountdownTimer from "../helpers/countdownTimer.js";

/**
 * The agent's canned welcome (the `messages: welcome:` string in the agent
 * script). When the visitor opens the chat BY ASKING A QUESTION (quick-question
 * chips, plan rows, ask lists, the proactive nudge), the welcome is redundant
 * noise between their question and the answer — the client swallows it.
 * The platform always sends it, so this must stay in sync with the agent
 * script's welcome text (prefix match; add entries if a French welcome or a
 * reworded welcome is configured).
 */
const WELCOME_MESSAGE_PREFIXES = [
    "Hi, my name is Lia",
];

/**
 * Whether a conversation entry is the agent's canned welcome message.
 * @param {object} conversationEntry
 * @returns {boolean}
 */
function isCannedWelcome(conversationEntry) {
    if (!ConversationEntryUtil.isTextMessage(conversationEntry)) {
        return false;
    }
    const text = (ConversationEntryUtil.getTextMessageContent(conversationEntry) || "").trim();
    return WELCOME_MESSAGE_PREFIXES.some((prefix) => text.startsWith(prefix));
}

/**
 * If every unit in a carousel envelope has the same bedroom count, return it
 * (so the Units page can pre-apply the matching filter). Null otherwise.
 * @param {object} envelope
 * @returns {number|null}
 */
function extractCarouselBeds(envelope) {
    try {
        const personalizations = envelope && Array.isArray(envelope.curation)
            && envelope.curation[0] && envelope.curation[0].personalizations;
        const data = personalizations && personalizations[0] && personalizations[0].data;
        if (!Array.isArray(data) || !data.length) {
            return null;
        }
        const beds = new Set(data.map((u) => Number(u.Bedrooms__c)).filter((n) => !Number.isNaN(n) && n > 0));
        return beds.size === 1 ? beds.values().next().value : null;
    } catch (e) {
        return null;
    }
}

export default function Conversation(props) {
    // Initialize a list of conversation entries.
    let [conversationEntries, setConversationEntries] = useState([]);
    // Initialize the conversation status.
    let [conversationStatus, setConversationStatus] = useState(CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION);
    // Tracks whether Pre-Chat form should be shown.
    let [showPrechatForm, setShowPrechatForm] = useState(false);
    // Tracks the most recent conversation message that was failed to send.
    let [failedMessage, setFailedMessage] = useState(undefined);

    // Initialize current participants that are actively typing (except end user).
    // Each key is the participant's `senderName` or `role` and each value is a reference to a CountdownTimer object.
    let [currentTypingParticipants, setCurrentTypingParticipants] = useState({});
    // Initialize whether at least 1 participant (not including end user) is typing.
    let [isAnotherParticipantTyping, setIsAnotherParticipantTyping] = useState(false);

    // AWAC: whether we are waiting for the agent's reply to a message we sent.
    // Server typing events are unreliable while the agent plans/calls actions,
    // so the client shows the thinking indicator from send until the agent's
    // message arrives (with a safety timeout so it can't get stuck).
    let [awaitingAgentReply, setAwaitingAgentReply] = useState(false);

    useEffect(() => {
        if (!awaitingAgentReply) {
            return undefined;
        }
        const timeoutId = setTimeout(() => setAwaitingAgentReply(false), 90000);
        return () => clearTimeout(timeoutId);
    }, [awaitingAgentReply]);

    // AWAC: the latest adaptive-web envelope to render in the SEPARATE gallery panel.
    // The chat stream is left untouched; the gallery opens beside it.
    let [galleryEnvelope, setGalleryEnvelope] = useState(undefined);
    let [galleryTemplateName, setGalleryTemplateName] = useState(undefined);

    // Broadcast the wing's open/closed state (and which template it shows) so
    // message rows can adapt — e.g. the "View matching units →" re-open link
    // only renders while the gallery is CLOSED. An effect (rather than
    // dispatching at every setter site) catches every open/close path.
    useEffect(() => {
        window.dispatchEvent(new CustomEvent("awac:gallery-state", {
            detail: { open: !!galleryEnvelope, template: galleryEnvelope ? (galleryTemplateName || null) : null }
        }));
    }, [galleryEnvelope, galleryTemplateName]);

    // AWAC: whether the AGENT participant has joined. A freshly OPENED
    // conversation exists BEFORE routing assigns the agent (~2s) — a message
    // sent in that window reaches nobody and only the welcome is processed,
    // so queued page messages must wait for the join.
    let [agentJoined, setAgentJoined] = useState(false);
    const agentJoinedRef = useRef(false);
    function markAgentJoined() {
        if (!agentJoinedRef.current) {
            agentJoinedRef.current = true;
            setAgentJoined(true);
        }
    }

    // AWAC: when a NEW conversation starts with a queued question, hide the
    // canned welcome — the visitor asked something specific; answer it first.
    const suppressWelcomeRef = useRef(false);
    // Testing aid: Date.now() of the last user send, consumed by the first
    // agent reply to compute its end-to-end response time.
    const pendingSendTimeRef = useRef(null);
    // The last user message TEXT, consumed by the first agent reply so the
    // unit summary can modulate its bullets to what the visitor asked about
    // (noise, view, dimensions, ...). Same lifecycle as pendingSendTimeRef.
    const pendingSendTextRef = useRef(null);

    useEffect(() => {
        let conversationStatePromise;

        // A restored conversation was already routed — the agent is in it.
        if (props.isExistingConversation) {
            markAgentJoined();
        } else if (typeof getAwacState("pendingMessage") === "string") {
            suppressWelcomeRef.current = true;
            setAwacState("welcomeSuppressed", true); // keep history consistent on restore
        }
        // NEW conversations boot for several seconds (create + routing +
        // agent join) before ANY reply — with a queued question that felt
        // broken ("nothing is happening"). Show the thinking indicator from
        // the very first paint; the welcome (or the queued question's answer)
        // clears it, and the 90s safety timeout backstops failures.
        if (!props.isExistingConversation) {
            setAwaitingAgentReply(true);
        }
        conversationStatePromise = props.isExistingConversation ? handleExistingConversation() : handleNewConversation();
        conversationStatePromise
        .then(() => {
            handleSubscribeToEventSource()
            .then(props.uiReady(true)) // Let parent (i.e. MessagingWindow) know the app is UI ready so that the parent can decide to show the actual Messaging window UI.
            .catch(() => {
                props.showMessagingWindow(false);
            })
        });

        return () => {
            conversationStatePromise
            .then(() => {
                cleanupMessagingData();
            });
        };
    }, []);

    /**
     * AWAC: send the queued page-context message (plan rows, CTA chips, ask
     * lists, the proactive nudge) if one is waiting. Only valid once the
     * conversation is OPENED and the agent has JOINED — earlier sends reach
     * an agent-less conversation and are never answered.
     */
    function flushPendingMessage() {
        if (conversationStatus !== CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION || !agentJoinedRef.current) {
            return;
        }
        const pending = getAwacState("pendingMessage");
        if (pending && typeof pending === "string") {
            clearAwacState("pendingMessage");
            handleSendTextMessage(getConversationId(), pending, util.generateUUID())
                .catch((err) => {
                    console.error(`Failed to send the queued page-context message: ${err && err.message ? err.message : err}`);
                });
        }
    }

    // Flush once BOTH conditions are met (status opened + agent joined).
    useEffect(() => {
        flushPendingMessage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationStatus, agentJoined]);

    // Fallback: if the join event never arrives (routing hiccup, replayed
    // stream), release the gate after 10s so the queued message isn't lost.
    useEffect(() => {
        if (conversationStatus !== CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION || agentJoined) {
            return undefined;
        }
        const timeoutId = setTimeout(markAgentJoined, 10000);
        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationStatus, agentJoined]);

    // AWAC: if the chat is ALREADY open when the landing page queues a message
    // (e.g. the user clicks a plan row mid-conversation), neither status nor
    // join state changes — so also flush on the open-chat event itself.
    useEffect(() => {
        window.addEventListener("awac:open-chat", flushPendingMessage);
        return () => window.removeEventListener("awac:open-chat", flushPendingMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationStatus]);

    /**
     * Update conversation status state based on the event from a child component i.e. MessagingHeader.
     * Updating conversation status state re-renders the current component as well as the child components and the child components can reactively use the updated conversation status to make any changes.
     *
     * @param {string} status - e.g. CLOSED.
     */
    function updateConversationStatus(status) {
        setConversationStatus(status);
    }

    /**
     * Handles a new conversation.
     *
     * 1. Fetch an Unauthenticated Access Token i.e. Messaging JWT.
     * 2. Create a new conversation.
     * @returns {Promise}
     */
    function handleNewConversation() {
        return handleGetUnauthenticatedJwt()
                .then(() => {
                    if (prechatUtil.shouldDisplayPrechatForm()) {
                        console.log("Pre-Chat is enabled. Continuing to render a Pre-Chat form.");
                        setShowPrechatForm(true);
                        return;
                    }
                    console.log("Pre-Chat is not enabled. Continuing to create a new conversation.");
                    return handleCreateNewConversation()
                            .then(() => {
                                console.log(`Completed initializing a new conversation with conversationId: ${getConversationId()}`);
                            })
                            .catch(err => {
                                console.error(`${err}`);
                            });
                })
                .catch(err => {
                    console.error(`${err}`);
                });
    }

    /**
     * Handles an existing conversation.
     *
     * 1. Fetch a Continuation Access Token i.e. Messaging JWT.
     * 2. Lists the available conversations and loads the current (also most-recent) conversation that is OPEN.
     * 3. Fetch the entries for the current conversation.
     * @returns {Promise}
     */
    function handleExistingConversation() {
        return handleGetContinuityJwt()
                .then(() => {
                    return handleListConversations()
                            .then(() => {
                                console.log(`Successfully listed the conversations.`);
                                handleListConversationEntries()
                                .then(console.log(`Successfully retrieved entries for the current conversation: ${getConversationId()}`))
                                .catch(err => {
                                    console.error(`${err}`);
                                });
                            })
                            .catch(err => {
                                console.error(`${err}`);
                            });
                })
                .catch(err => {
                    console.error(`${err}`);
                });
    }

    /**
     * Handles fetching an Unauthenticated Access Token i.e. Messaging JWT.
     *
     * 1. If a JWT already exists, simply return.
     * 2. Makes a request to Unauthenticated Access Token endpoint.
     * 3. Updates the web storage with the latest JWT.
     * 4. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
     * @returns {Promise}
     */
    function handleGetUnauthenticatedJwt() {
        if (getJwt()) {
            console.warn("Messaging access token (JWT) already exists in the web storage. Discontinuing to create a new Unauthenticated access token.");
            return handleExistingConversation().then(Promise.reject());
        }

        return getUnauthenticatedAccessToken()
                .then((response) => {
                    console.log("Successfully fetched an Unauthenticated access token.");
                    // Parse the response object which includes access-token (JWT), configutation data.
                    if (typeof response === "object") {
                        setJwt(response.accessToken);
                        setItemInWebStorage(STORAGE_KEYS.JWT, response.accessToken);
                        setLastEventId(response.lastEventId);
                        setDeploymentConfiguration(response.context && response.context.configuration && response.context.configuration.embeddedServiceConfig);
                    }    
                })
                .catch((err) => {
                    console.error(`Something went wrong in fetching an Unauthenticated access token: ${err && err.message ? err.message : err}`);
                    handleMessagingErrors(err);
                    cleanupMessagingData();
                    props.showMessagingWindow(false);
                    throw new Error("Failed to fetch an Unauthenticated access token.");
                });
    }

    /**
     * Handles creating a new conversation.
     *
     * 1. If a conversation is already open, simply return.
     * 2. Generate a new unique conversation-id and initialize in-memory.
     * 3. Makes a request to Create Conversation endpoint.
     * 4. Updates the conversation status internally to OPENED, for the associated components to reactively update.
     * 5. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
     * @returns {Promise}
     */
    function handleCreateNewConversation(routingAttributes) {
        if (conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION) {
            console.warn("Cannot create a new conversation while a conversation is currently open.");
            return Promise.reject();
        }

        // Initialize a new unique conversation-id in-memory.
        storeConversationId(util.generateUUID());
        return createConversation(getConversationId(), routingAttributes)
                .then(() => {
                    console.log(`Successfully created a new conversation with conversation-id: ${getConversationId()}`);
                    updateConversationStatus(CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION);
                    props.showMessagingWindow(true);
                })
                .catch((err) => {
                    console.error(`Something went wrong in creating a new conversation with conversation-id: ${getConversationId()}: ${err && err.message ? err.message : err}`);
                    handleMessagingErrors(err);
                    cleanupMessagingData();
                    props.showMessagingWindow(false);
                    throw new Error("Failed to create a new conversation.");
                });
    }

    /**
     * Handles fetching a Continuation Access Token i.e. Messaging JWT.
     *
     * 1. Makes a request to Continuation Access Token endpoint.
     * 2. Updates the web storage with the latest JWT.
     * 3. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
     * @returns {Promise}
     */
    function handleGetContinuityJwt() {
        return getContinuityJwt()
                .then((response) => {
                    setJwt(response.accessToken);
                    setItemInWebStorage(STORAGE_KEYS.JWT, response.accessToken);
                })
                .catch((err) => {
                    console.error(`Something went wrong in fetching a Continuation Access Token: ${err && err.message ? err.message : err}`);
                    handleMessagingErrors(err);
                    throw new Error("Failed to fetch a Continuation access token.");
                });
    }

    /**
     * Handles fetching a list of all conversations available. This returns only conversations which are OPEN, unless otherwise specified in the request.
     *
     * 1. Makes a request to List Conversations endpoint.
     * 2. If there are multiple OPEN conversations, loads the conversation with the most-recent start time.
     * 3. Performs a cleanup - clears messaging data and closes the Messaging Window, if the request is unsuccessful.
     * @returns {Promise}
     */
    function handleListConversations() {
        return listConversations()
                .then((response) => {
                    if (response && response.openConversationsFound > 0 && response.conversations.length) {
                        const openConversations = response.conversations;
                        if (openConversations.length > 1) {
				            console.warn(`Expected the user to be participating in 1 open conversation but instead found ${openConversations.length}. Loading the conversation with latest startTimestamp.`);
				            openConversations.sort((conversationA, conversationB) => conversationB.startTimestamp - conversationA.startTimestamp);
                        }
                        // Update conversation-id with the one from service.
                        storeConversationId(openConversations[0].conversationId);
                        updateConversationStatus(CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION);
                        props.showMessagingWindow(true);
                    } else {
                        // No open conversations found.
                        cleanupMessagingData();
                        props.showMessagingWindow(false);
                    }
                })
                .catch((err) => {
                    console.error(`Something went wrong in fetching a list of conversations: ${err && err.message ? err.message : err}`);
                    handleMessagingErrors(err);
                    throw new Error("Failed to list the conversations.");
                });
    }

    /**
     * Handles fetching a list of all conversation entries for the current conversation.
     *
     * 1. Makes a request to List Conversation Entries endpoint.
     * 2. Renders the conversation entries based on their Entry Type.
     * @returns {Promise}
     */
    function handleListConversationEntries() {
        return listConversationEntries(getConversationId())
                .then((response) => {
                    if (Array.isArray(response)) {
                        response.reverse().forEach(entry => {
                            const conversationEntry = generateConversationEntryForCurrentConversation(entry);
                            if (!conversationEntry) {
                                return;
                            }
    
                            switch (conversationEntry.entryType) {
                                case CONVERSATION_CONSTANTS.EntryTypes.CONVERSATION_MESSAGE:
                                    conversationEntry.isEndUserMessage = ConversationEntryUtil.isMessageFromEndUser(conversationEntry);
                                    // Keep history consistent: a welcome hidden
                                    // at open stays hidden on session restore.
                                    if (!conversationEntry.isEndUserMessage && getAwacState("welcomeSuppressed") && isCannedWelcome(conversationEntry)) {
                                        break;
                                    }
                                    addConversationEntry(conversationEntry);
                                    break;
                                case CONVERSATION_CONSTANTS.EntryTypes.PARTICIPANT_CHANGED:
                                case CONVERSATION_CONSTANTS.EntryTypes.ROUTING_RESULT:
                                    addConversationEntry(conversationEntry);
                                    break;
                                default:
                                    console.log(`Unrecognized conversation entry type: ${conversationEntry.entryType}.`);
                            }
                        });
                    } else {
                        console.error(`Expecting a response of type Array from listConversationEntries but instead received: ${response}`);
                    }
                })
                .catch((err) => {
                    console.error(`Something went wrong while processing entries from listConversationEntries response:  ${err && err.message ? err.message : err}`);
                    handleMessagingErrors(err);
                    throw new Error("Failed to list the conversation entries for the current conversation.");
                });
    }

    /**
     * Handles establishing a connection to the EventSource i.e. SSE.
     * Selectively listens to the supported events in the app by adding the corresponding event listeners.
     * Note: Update the list of events/event-listeners to add/remove support for the available events. Refer https://developer.salesforce.com/docs/service/messaging-api/references/about/server-sent-events-structure.html
     * @returns {Promise}
     */
    function handleSubscribeToEventSource() {
        return subscribeToEventSource({
                    [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_MESSAGE]: handleConversationMessageServerSentEvent,
                    [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_ROUTING_RESULT]: handleRoutingResultServerSentEvent,
                    [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_PARTICIPANT_CHANGED]: handleParticipantChangedServerSentEvent,
                    [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_TYPING_STARTED_INDICATOR]: handleTypingStartedIndicatorServerSentEvent,
                    [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_TYPING_STOPPED_INDICATOR]: handleTypingStoppedIndicatorServerSentEvent,
                    [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_DELIVERY_ACKNOWLEDGEMENT]: handleConversationDeliveryAcknowledgementServerSentEvent,
                    [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_READ_ACKNOWLEDGEMENT]: handleConversationReadAcknowledgementServerSentEvent,
                    [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_CLOSE_CONVERSATION]: handleCloseConversationServerSentEvent
                })
                .then(() => {
                    console.log("Subscribed to the Event Source (SSE).");
                })
                .catch((err) => {
                    handleMessagingErrors(err);
                    throw new Error(err);
                });
    }

    /**
     * Generate a Conversation Entry object from the server sent event.
     *
     * 1. Create a Conversation Entry object from the parsed event data.
     * 2. Return the Conversation Entry if the conversationEntry is for the current conversation and undefined, otherwise.
     * @param {object} event - Event data payload from server-sent event.
     * @returns {object|undefined}
     */
    function generateConversationEntryForCurrentConversation(parsedEventData) {
        const conversationEntry = ConversationEntryUtil.createConversationEntry(parsedEventData);

        // Handle server sent events only for the current conversation
        if (parsedEventData.conversationId === getConversationId()) {
            return conversationEntry;
        }
        console.log(`Current conversation-id: ${getConversationId()} does not match the conversation-id in server sent event: ${parsedEventData.conversationId}. Ignoring the event.`);
        return undefined;
    }

    /**
     * Adds a Conversation Entry object to the list of conversation entries. Updates the state of the list of conversation entries for the component(s) to be updated in-turn, reactively.
     * @param {object} conversationEntry - entry object for the current conversation.
     */
    function addConversationEntry(conversationEntry) {
        conversationEntries.push(conversationEntry);
        setConversationEntries([...conversationEntries]);
    }

    /**
     * Handle a CONVERSATION_MESSAGE server-sent event.
     *
     * This includes:
     *  1. Parse, populate, and create ConversationEntry object based on its entry type
     *      NOTE: Skip processing CONVERSATION_MESSAGE if the newly created ConversationEntry is undefined or invalid or not from the current conversation.
     *  2. Updates in-memory list of conversation entries and the updated list gets reactively passed on to MessagingBody.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleConversationMessageServerSentEvent(event) {
        try {
            console.log(`Successfully handling conversation message server sent event.`);
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            const conversationEntry = generateConversationEntryForCurrentConversation(parsedEventData);
            if (!conversationEntry) {
                return;
            }

            if (ConversationEntryUtil.isMessageFromEndUser(conversationEntry)) {
                conversationEntry.isEndUserMessage = true;

                // Since message is echoed back by the server, mark the conversation entry as sent.
                conversationEntry.isSent = true;

                console.log(`End user successfully sent a message.`);
            } else {
                conversationEntry.isEndUserMessage = false;
                // The agent replied — stop the thinking indicator.
                setAwaitingAgentReply(false);
                // A bot/agent message proves the agent is in the conversation
                // (covers replays where the participant event never re-fires).
                markAgentJoined();
                // Conversation opened via a quick question: swallow the canned
                // welcome so the visitor sees their question answered first.
                // Only the FIRST bot message is ever a candidate.
                if (suppressWelcomeRef.current) {
                    suppressWelcomeRef.current = false;
                    if (isCannedWelcome(conversationEntry)) {
                        console.log(`Skipped the canned welcome (conversation opened via a quick question).`);
                        return;
                    }
                }
                // Testing aid: attach the end-to-end response time to the FIRST
                // agent message answering the pending user message (placed after
                // the welcome suppression so a swallowed welcome can't consume it).
                if (pendingSendTimeRef.current) {
                    conversationEntry.responseTimeMs = Date.now() - pendingSendTimeRef.current;
                    pendingSendTimeRef.current = null;
                }
                // Attach the question this reply answers, so the unit summary
                // can surface the facts the visitor actually asked about.
                if (pendingSendTextRef.current) {
                    conversationEntry.answeredQuestion = pendingSendTextRef.current;
                    pendingSendTextRef.current = null;
                }
                // AWAC: when the agent paraphrases a booking confirmation
                // instead of relaying the envelope verbatim, synthesize the
                // BookingConfirm envelope locally (deterministic fallback).
                applyBookingConfirmFallback(conversationEntry);
                console.log(`Successfully received a message from ${conversationEntry.actorType}`);
            }

            addConversationEntry(conversationEntry);
        } catch(err) {
            console.error(`Something went wrong in handling conversation message server sent event: ${err}`);
        }
    }

    /**
     * Handle a ROUTING_RESULT server-sent event.
     *
     * This includes:
     *  1. Parse, populate, and create ConversationEntry object based on its entry type.
     *      NOTE: Skip processing ROUTING_RESULT if the newly created ConversationEntry is undefined or invalid or not from the current conversation.
     *  2. Updates in-memory list of conversation entries and the updated list gets reactively passed on to MessagingBody.
     *
     *  NOTE: Update the chat client based on the latest routing result. E.g. if the routing type is transfer, set an internal flag like `isTransferring` to 'true' and use that to show a transferring indicator in the ui.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleRoutingResultServerSentEvent(event) {
        try {
            console.log(`Successfully handling routing result server sent event.`);
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            const conversationEntry = generateConversationEntryForCurrentConversation(parsedEventData);
            if (!conversationEntry) {
                return;
            }

            if (conversationEntry.messageType === CONVERSATION_CONSTANTS.RoutingTypes.INITIAL) {
                // Render reasonForNotRouting when initial routing fails.
                switch (conversationEntry.content.failureType) {
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.NO_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.SUBMISSION_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.ROUTING_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.UNKNOWN_ERROR:
                        addConversationEntry(conversationEntry);
                        break;
                    default:
                        console.error(`Unrecognized initial routing failure type: ${conversationEntry.content.failureType}`);
                }
                // Handle when a conversation is being transferred.
            } else if (conversationEntry.messageType === CONVERSATION_CONSTANTS.RoutingTypes.TRANSFER) {
                switch (conversationEntry.content.failureType) {
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.NO_ERROR:
                        // Render transfer timestamp when transfer is requested successfully.
                        // TODO: Add a transfer state ui update.
                        addConversationEntry(conversationEntry);
                        break;
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.SUBMISSION_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.ROUTING_ERROR:
                    case CONVERSATION_CONSTANTS.RoutingFailureTypes.UNKNOWN_ERROR:
                        break;
                    default:
                        console.error(`Unrecognized transfer routing failure type: ${conversationEntry.content.failureType}`);
                }
            } else {
                console.error(`Unrecognized routing type: ${conversationEntry.messageType}`);
            }
        } catch (err) {
            console.error(`Something went wrong in handling routing result server sent event: ${err}`);
        }
    }

    /**
     * Handle a PARTICIPANT_CHANGED server-sent event.
     *
     * This includes:
     *  1. Parse, populate, and create ConversationEntry object based on its entry type.
     *      NOTE: Skip processing PARTICIPANT_CHANGED if the newly created ConversationEntry is undefined or invalid or not from the current conversation.
     *  2. Updates in-memory list of conversation entries and the updated list gets reactively passed on to MessagingBody.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleParticipantChangedServerSentEvent(event) {
        try {
            console.log(`Successfully handling participant changed server sent event.`);
            // The agent (Lia) joining is the participant change at conversation
            // start — release the queued page-context message.
            markAgentJoined();
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            const conversationEntry = generateConversationEntryForCurrentConversation(parsedEventData);
            if (!conversationEntry) {
                return;
            }
            addConversationEntry(conversationEntry);
        } catch (err) {
            console.error(`Something went wrong in handling participant changed server sent event: ${err}`);
        }
    }

    /**
     * Handle a TYPING_STARTED_INDICATOR server-sent event.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleTypingStartedIndicatorServerSentEvent(event) {
        try {
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            // Handle typing indicators only for the current conversation
            if (getConversationId() === parsedEventData.conversationId) {
                const senderName = ConversationEntryUtil.getSenderDisplayName(parsedEventData) || ConversationEntryUtil.getSenderRole(parsedEventData);
                const typingParticipantTimer = currentTypingParticipants[senderName] && currentTypingParticipants[senderName].countdownTimer;

                // If we have received typing indicator from this sender within the past 5 seconds, reset the timer
                // Otherwise, start a new timer
                if (ConversationEntryUtil.getSenderRole(parsedEventData) !== CONVERSATION_CONSTANTS.ParticipantRoles.ENDUSER) {
                    console.log(`Successfully handling typing started indicator server sent event.`);

                    if (typingParticipantTimer) {
                        typingParticipantTimer.reset(Date.now());
                    } else {
                        currentTypingParticipants[senderName] = {
                            countdownTimer: new CountdownTimer(() => {
                                delete currentTypingParticipants[senderName];

                                if (!Object.keys(currentTypingParticipants).length) {
                                    setIsAnotherParticipantTyping(false);
                                }
                            }, CLIENT_CONSTANTS.TYPING_INDICATOR_DISPLAY_TIMEOUT, Date.now()),
                            role: parsedEventData.conversationEntry.sender.role
                        };

                        currentTypingParticipants[senderName].countdownTimer.start();
                    }

                    setIsAnotherParticipantTyping(true);
                }
            }
        } catch (err) {
            console.error(`Something went wrong in handling typing started indicator server sent event: ${err}`);
        }
    }

    /**
     * Handle a TYPING_STOPPED_INDICATOR server-sent event.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleTypingStoppedIndicatorServerSentEvent(event) {
        try {
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }           

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            // Handle typing indicators only for the current conversation
            if (getConversationId() === parsedEventData.conversationId) {
                const senderName = ConversationEntryUtil.getSenderDisplayName(parsedEventData) || ConversationEntryUtil.getSenderRole(parsedEventData);
    
                delete currentTypingParticipants[senderName];

                if (!Object.keys(currentTypingParticipants).length) {
                    setIsAnotherParticipantTyping(false);
                }
            }
        } catch (err) {
            console.error(`Something went wrong in handling typing stopped indicator server sent event: ${err}`);
        }
    }

    /**
     * Handle a server-sent event CONVERSATION_DELIVERY_ACKNOWLEDGEMENT:
     * - Parse server-sent event and update a conversation entry (if it exists) with data from the event.
     * - Store delivery acknowledgement timestamp on conversation entry to show receipt timestamp.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleConversationDeliveryAcknowledgementServerSentEvent(event) {
        try {
            console.log(`Successfully handling conversation delivery acknowledgement server sent event.`);
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            // Handle delivery acknowledgements only for the current conversation
            if (getConversationId() === parsedEventData.conversationId) {
                if (parsedEventData.conversationEntry && parsedEventData.conversationEntry.relatedRecords.length && parsedEventData.conversationEntry.entryPayload.length) {
                    // Store acknowledgement timestamp and identifier of message that was delivered.
                    const deliveredMessageId = parsedEventData.conversationEntry.relatedRecords[0];
                    const deliveryAcknowledgementTimestamp = JSON.parse(parsedEventData.conversationEntry.entryPayload).acknowledgementTimestamp;

                    // Make deep copy of conversation entries to find matching messageId. 
                    util.createDeepCopy(conversationEntries).filter((conversationEntry) => {
                        if (conversationEntry.messageId === deliveredMessageId) {
                            conversationEntry.isDelivered = true;
                            conversationEntry.deliveryAcknowledgementTimestamp = deliveryAcknowledgementTimestamp;
                        }
                    });
                }
            }
        } catch (err) {
            console.error(`Something went wrong in handling conversation delivery acknowledgement server sent event: ${err}`);
        }
    }

    /**
     * Handle a server-sent event CONVERSATION_READ_ACKNOWLEDGEMENT:
     * - Parse server-sent event and update a conversation entry (if it exists) with data from the event.
     * - Store read acknowledgement timestamp on conversation entry to show receipt timestamp.
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleConversationReadAcknowledgementServerSentEvent(event) {
        try {
            console.log(`Successfully handling conversation read acknowledgement server sent event.`);
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);
            // Handle read acknowledgements only for the current conversation
            if (getConversationId() === parsedEventData.conversationId) {
                if (parsedEventData.conversationEntry && parsedEventData.conversationEntry.relatedRecords.length && parsedEventData.conversationEntry.entryPayload.length) {
                    // Store acknowledgement timestamp and identifier of message that was read.
                    const readMessageId = parsedEventData.conversationEntry.relatedRecords[0];
                    const readAcknowledgementTimestamp = JSON.parse(parsedEventData.conversationEntry.entryPayload).acknowledgementTimestamp;

                    // Make deep copy of conversation entries to find matching messageId. 
                    util.createDeepCopy(conversationEntries).filter((conversationEntry) => {
                        if (conversationEntry.messageId === readMessageId) {
                            conversationEntry.isRead = true;
                            conversationEntry.readAcknowledgementTimestamp = readAcknowledgementTimestamp;
                        }
                    });
                }
            }
        } catch (err) {
            console.error(`Something went wrong in handling conversation read acknowledgement server sent event: ${err}`);
        }
    }

    /**
     * Handle a CONVERSATION_CLOSED server-sent event.
     *
     * @param {object} event - Event data payload from server-sent event.
     */
    function handleCloseConversationServerSentEvent(event) {
        try {
            console.log(`Successfully handling close conversation server sent event.`);
            // Update in-memory to the latest lastEventId
            if (event && event.lastEventId) {
                setLastEventId(event.lastEventId);
            }

            const parsedEventData = ConversationEntryUtil.parseServerSentEventData(event);

            // Do not render conversation ended text if the conversation entry is not for the current conversation.
            if (getConversationId() === parsedEventData.conversationId) {
                // Update state to conversation closed status.
                updateConversationStatus(CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION);
            }
        } catch (err) {
            console.error(`Something went wrong while handling conversation closed server sent event in conversation ${getConversationId()}: ${err}`);
        }
    }

    /**
     * Handle sending a static text message in a conversation.
     * @param {string} conversationId - Identifier of the conversation.
     * @param {string} value - Actual text of the message.
     * @param {string} messageId - Unique identifier of the message.
     * @param {string} inReplyToMessageId - Identifier of another message where this message is being replied to.
     * @param {boolean} isNewMessagingSession - Whether it is a new messaging session.
     * @param {object} routingAttributes - Pre-Chat fields and its values, if configured.
     * @param {object} language - language code.
     * 
     * @returns {Promise}
     */
    function handleSendTextMessage(conversationId, value, messageId, inReplyToMessageId, isNewMessagingSession, routingAttributes, language) {
        // Show the thinking indicator until the agent's reply arrives.
        setAwaitingAgentReply(true);
        // Testing aid: stamp the send time so the agent's next reply can show
        // its end-to-end response time (send -> reply received).
        pendingSendTimeRef.current = Date.now();
        pendingSendTextRef.current = value;
        return sendTextMessage(conversationId, value, messageId, inReplyToMessageId, isNewMessagingSession, routingAttributes, language)
                .catch((err) => {
                    console.error(`Something went wrong while sending a message to conversation ${conversationId}: ${err}`);
                    setAwaitingAgentReply(false);
                    pendingSendTimeRef.current = null;
                    pendingSendTextRef.current = null;
                    setFailedMessage(Object.assign({}, {messageId, value, inReplyToMessageId, isNewMessagingSession, routingAttributes, language}));
                    handleMessagingErrors(err);
                });
    }

    /**
     * AWAC: handle an action triggered from an adaptive-web component (e.g. an
     * option button inside FloorPlanShowcase). Sends the reply text back to the
     * agent as a normal text message so the agent stays in the loop.
     * @param {string} text
     * @returns {Promise}
     */
    function handleAwacAction(text) {
        if (!text || typeof text !== "string") {
            return Promise.resolve();
        }
        const messageId = util.generateUUID();
        return handleSendTextMessage(getConversationId(), text, messageId);
    }

    /**
     * AWAC: handle a structured action emitted by a gallery template (e.g. the
     * "Book a visit" button on a unit card). Maps the action object to plain
     * text and routes it through the normal chat send path so the agent stays
     * in the loop.
     * @param {object|string} action
     * @returns {Promise}
     */
    function handleGalleryAction(action) {
        if (typeof action === "string") {
            track(AwacEvents.GALLERY_ACTION, { action });
            return handleAwacAction(action);
        }
        if (action && action.type === "book_visit" && action.unitName) {
            track(AwacEvents.GALLERY_ACTION, { action: action.type, unit: action.unitName });
            return handleAwacAction(`I'd like to book a visit for ${action.unitName}`);
        }
        return Promise.resolve();
    }

    /**
     * AWAC: called when an adaptive-web envelope arrives (or its "View floor
     * plans" affordance is clicked). Opens the latest envelope in a SEPARATE
     * gallery panel docked beside the chat. The chat window itself is left
     * untouched (no inline gallery, no forced resize).
     * @param {object} envelope - parsed AWAC envelope.
     * @param {string} templateName - registered template name.
     */
    function handleAwacRender(envelope, templateName) {
        if (!envelope) {
            return;
        }
        const resolvedName = templateName || (Array.isArray(envelope.template) && envelope.template[0] && envelope.template[0].name);
        // PoiMapControl drives the FULL-SCREEN map on the Neighborhood page
        // instead of the in-chat gallery wing: forward the command to the
        // poiMapBridge (which navigates to the page and postMessages the map
        // iframe). The map is now the stage, so the widget resets to its
        // normal floating card: close the gallery wing and drop out of
        // fullscreen. The chat stays open beside the map (the map reserves
        // space for it) so the conversation continues naturally.
        if (resolvedName === AWAC_CONSTANTS.Templates.POI_MAP_CONTROL) {
            const command = envelope.options && !Array.isArray(envelope.options) && envelope.options.command;
            if (command) {
                sendMapCommand(command);
                track(AwacEvents.ENVELOPE_RENDERED, { template: resolvedName });
                setGalleryEnvelope(undefined);
                setGalleryTemplateName(undefined);
                if (typeof props.onGalleryActiveChange === "function") {
                    props.onGalleryActiveChange(false);
                }
                if (typeof props.exitFullscreen === "function") {
                    props.exitFullscreen();
                }
            }
            return;
        }
        // Only open the panel for templates we can actually render.
        if (!AwacTemplateRegistry.getAwacTemplate(resolvedName)) {
            return;
        }
        // Phones: unit results do NOT auto-open the sheet over the chat (that
        // read as buggy). Each unit block in the message carries a "View Plan"
        // button that mounts the carousel INLINE in the thread instead. Other
        // templates (booking, floor plans) keep the sheet.
        if (resolvedName === AWAC_CONSTANTS.Templates.UNIT_CAROUSEL
            && window.matchMedia && window.matchMedia("(max-width: 640px)").matches) {
            track(AwacEvents.ENVELOPE_RENDERED, { template: resolvedName });
            const beds = extractCarouselBeds(envelope);
            if (beds) {
                setAwacState("unitsFilterBeds", beds);
            }
            return;
        }
        setGalleryEnvelope(envelope);
        setGalleryTemplateName(resolvedName);
        track(AwacEvents.ENVELOPE_RENDERED, { template: resolvedName });
        // Navigation-aware agent: when the agent shows units or floor plans,
        // also take the visitor to the Units page (App listens for this). If
        // the carousel is filtered to one bedroom count, the Units page
        // arrives pre-filtered to the same set.
        if (resolvedName === AWAC_CONSTANTS.Templates.UNIT_CAROUSEL
            || resolvedName === AWAC_CONSTANTS.Templates.FLOOR_PLAN_SHOWCASE) {
            const beds = extractCarouselBeds(envelope);
            if (beds) {
                setAwacState("unitsFilterBeds", beds);
            }
            window.dispatchEvent(new CustomEvent("awac:show-units", { detail: { beds } }));
        }
        // Tell the parent window to expand horizontally so the gallery wing has room.
        if (typeof props.onGalleryActiveChange === "function") {
            props.onGalleryActiveChange(true);
        }
    }

    /**
     * AWAC: dismiss the gallery wing. Leaves the conversation fully intact and
     * collapses the widget back to its single-column chat width.
     */
    function handleCloseGallery() {
        setGalleryEnvelope(undefined);
        setGalleryTemplateName(undefined);
        if (typeof props.onGalleryActiveChange === "function") {
            props.onGalleryActiveChange(false);
        }
    }

    /**
     * Close messaging window handler for the event from a child component i.e. MessagingHeader.
     * When such event is received, invoke the parent's handler to close the messaging window if the conversation status is closed or not yet started.
     */
    function endConversation() {
        if (conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.OPENED_CONVERSATION) {
            // End the conversation if it is currently opened.
            return closeConversation(getConversationId())
                .then(() => {
                    console.log(`Successfully closed the conversation with conversation-id: ${getConversationId()}`);
                })
                .catch((err) => {
                    console.error(`Something went wrong in closing the conversation with conversation-id ${getConversationId()}: ${err}`);
                })
                .finally(() => {
                    cleanupMessagingData();
                });
        }
    }

    /**
     * Close messaging window handler for the event from a child component i.e. MessagingHeader.
     * When such event is received, invoke the parent's handler to close the messaging window if the conversation status is closed or not yet started.
     */
    function closeMessagingWindow() {
        if (conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION || conversationStatus === CONVERSATION_CONSTANTS.ConversationStatus.NOT_STARTED_CONVERSATION) {
            props.showMessagingWindow(false);
        }
    }

    /**
     * Performs a cleanup in the app.
     * 1. Closes the EventSource connection.
     * 2. Clears the web storage.
     * 3. Clears the in-memory messaging data.
     * 4. Update the internal conversation status to CLOSED.
     */
    function cleanupMessagingData() {
        closeEventSource()
        .then(console.log("Closed the Event Source (SSE)."))
        .catch((err) => {
            console.error(`Something went wrong in closing the Event Source (SSE): ${err}`);
        });

        clearWebStorage();
        clearInMemoryData();
        // AWAC: the booking chip and any queued page-context message belong to
        // the conversation that just ended — clear them with it.
        clearAllAwacState();

        // Update state to conversation closed status.
        updateConversationStatus(CONVERSATION_CONSTANTS.ConversationStatus.CLOSED_CONVERSATION);
    }

    /**
     * Handles the errors from messaging endpoint requests.
     * If a request is failed due to an Unauthorized error (i.e. 401), peforms a cleanup and resets the app and console logs otherwise.
     */
    function handleMessagingErrors(err) {
        if (typeof err === "object") {
            if (err.status) {
                switch (err.status) {
                    case 401:
                        console.error(`Unauthenticated request: ${err.message}`);
                        cleanupMessagingData();
                        props.showMessagingWindow(false);
                        break;
                    case 400:
                        console.error(`Invalid request parameters. Please check your data before retrying: ${err.message}`);
                        break;
                    case 404:
                        console.error(`Resource not found. Please check your data before retrying: ${err.message}`);
                        break;
                    case 429:
                        console.warn(`Too many requests issued from the app. Try again in sometime: ${err.message}`);
                        break;
                    /**
                     * HTTP error code returned by the API(s) when a message is sent and failed because no messaging session exists. This error indicates client
                     * to surface the Pre-Chat form if configured to show for every new messaging session and then retry the failed message with routingAttributes.
                     */
                    case 417:
                        if (prechatUtil.shouldDisplayPrechatForm() && prechatUtil.shouldDisplayPrechatEveryMessagingSession()) {
                            console.log("Pre-Chat configured to show for every new messaging session. Continuing to display the Pre-Chat form.");
                            setShowPrechatForm(true);
                        }
                    case 500:
                        console.error(`Something went wrong in the request, please try again: ${err.message}`);
                        break;
                    default:
                        console.error(`Unhandled/Unknown http error: ${err}`);
                        cleanupMessagingData();
                        props.showMessagingWindow(false);
                }
                return;
            }
            console.error(`Something went wrong: ${err && err.message ? err.message : err}`);
        }
        return;
    }

    /**
     * Handles submitting a Pre-Chat form to either start a new conversation or a new messaging session.
     * @param {object} prechatData - Object containing key-value pairs of Pre-Chat form fields and their corresponding values.
     */
    function handlePrechatSubmit(prechatData) {
        let prechatSubmitPromise;
        console.log(`Pre-Chat fields values on submit: ${prechatData}.`);

        // If there is a failed message being tracked while submitting Pre-Chat form, consider it is an existing conversation but a new messaging session. Resend the failed message with the routing attributes to begin a new messaging session.
        if (failedMessage) {
            prechatSubmitPromise = handleSendTextMessage(getConversationId(), failedMessage.value, failedMessage.messageId, failedMessage.inReplyToMessageId, true, prechatData, failedMessage.language);
        } else {
            // If there is no failed message being tracked while submitting Pre-Chat form, create a new conversation.
            prechatSubmitPromise = handleCreateNewConversation(prechatData);
        }
        prechatSubmitPromise
        .then(() => {
            setShowPrechatForm(false);
        });
    }

    return (
        <div className="awacWidgetRow">
            {/* Left column: the chat. Keeps its own fixed width; never restructured. */}
            <div className="awacChatColumn">
                <MessagingHeader
                    conversationStatus={conversationStatus}
                    endConversation={endConversation}
                    minimizeMessagingWindow={props.minimizeMessagingWindow}
                    isFullscreen={props.isFullscreen}
                    onToggleFullscreen={props.onToggleFullscreen}
                    closeMessagingWindow={closeMessagingWindow} />
                {!showPrechatForm &&
                <>
                    <MessagingBody
                        conversationEntries={conversationEntries}
                        conversationStatus={conversationStatus} 
                        typingParticipants={currentTypingParticipants}
                        showTypingIndicator={isAnotherParticipantTyping || awaitingAgentReply}
                        onAwacAction={handleAwacAction}
                        onAwacRender={handleAwacRender} />
                    <MessagingInputFooter
                        conversationStatus={conversationStatus}
                        sendTextMessage={handleSendTextMessage}
                        hasUserMessaged={conversationEntries.some((entry) => entry.isEndUserMessage)}
                        sendTypingIndicator={sendTypingIndicator} />
                </>
                }
                {
                    showPrechatForm &&
                    <Prechat prechatSubmit={handlePrechatSubmit} />
                }
            </div>
            {/* Right column: the gallery wing. Slides open inside the SAME container. */}
            {
                galleryEnvelope &&
                <GalleryPanel
                    envelope={galleryEnvelope}
                    templateName={galleryTemplateName}
                    onClose={handleCloseGallery}
                    onAction={handleGalleryAction} />
            }
        </div>
    );
}